import { ActionEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/action-entity.enum";
import { EntityEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/entity.enum";
import EntityFieldsCatalogAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/field-catalog.entity";
import { CommonModule } from "@angular/common";
import { Component, Inject } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTabsModule } from "@angular/material/tabs";
import { IAPIItemResult } from "app/common/interfaces";
import { ToggleOptionComponent } from "app/shared/components/form-ui/toggle-option/toggle-option.component";
import { combineLatest, map } from "rxjs";
import { CompanyFieldCatalogService } from "./company-form-config.service";
import {
  Field,
  UpdateFieldPayload,
} from "./interfaces/field-catalog.interface";

@Component({
  selector: "app-company-form-config",
  templateUrl: "./company-form-config.component.html",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTabsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    ToggleOptionComponent,
  ],
  styles: [
    `
      ::ng-deep .mat-mdc-tab-body-content {
        padding: 10px !important;
      }
    `,
  ],
})
export class CompanyFormConfigComponent {
  form: FormGroup;
  entities: EntityEnum[] = [];
  formConfigs: Record<string, { Create: FormGroup; Update: FormGroup }> = {};
  selectedEntityIndex: number = 0;
  loading = true;

  readonly entityLabels: Record<EntityEnum, string> = {
    [EntityEnum.BUSINESS_PARTNER]: "Nuevos prospectos",
    [EntityEnum.CREDIT_APPLICATION]: "Solicitud de crédito",
    [EntityEnum.CREATED_BUSINESS_PARTNER]: "Socios de Negocio creados",
  };

  constructor(
    private readonly companyFieldCatalogService: CompanyFieldCatalogService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<CompanyFormConfigComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      companyId: string;
      entities: EntityEnum[];
    },
  ) {}

  ngOnInit(): void {
    this.entities = this.data.entities;
    this.loadAllConfigs();
  }

  loadAllConfigs() {
    const companyId = this.data.companyId;
    const requests = [];

    for (const entity of this.entities) {
      requests.push(
        this.companyFieldCatalogService
          .getFields(companyId, entity, ActionEnum.CREATE)
          .pipe(
            map((res: IAPIItemResult<EntityFieldsCatalogAttributes>) => {
              const data = res.data;
              return data.isActive ? data : null;
            }),
          ),
      );
      requests.push(
        this.companyFieldCatalogService
          .getFields(companyId, entity, ActionEnum.UPDATE)
          .pipe(
            map((res: IAPIItemResult<EntityFieldsCatalogAttributes>) => {
              const data = res.data;
              return data.isActive ? data : null;
            }),
          ),
      );
    }

    combineLatest(requests).subscribe((results) => {
      const activeResults = results.filter((result) => result !== null);
      for (const result of activeResults) {
        if (!this.formConfigs[result.entity]) {
          this.formConfigs[result.entity] = { Create: null, Update: null };
        }

        this.formConfigs[result.entity][result.action] = this.fb.group({
          id: result.id,
          fields: this.fb.array(
            (result.fields || []).map((field) => this.createFieldGroup(field)),
          ),
        });
      }

      this.loading = false;
    });
  }

  createFieldGroup(field: Field): FormGroup {
    const group = this.fb.group({
      _id: [field._id],
      name: [field.name],
      label: [field.label],
      type: [field.type],
      visible: [{ value: !!field.visible, disabled: field.mandatory }],
      required: [{ value: !!field.required, disabled: field.mandatory }],
      mandatory: [field.mandatory],
      _original: [
        JSON.stringify({
          label: field.label,
          visible: field.visible,
          required: field.required,
        }),
      ],
    });

    return group;
  }

  getEntityLabel(entity: EntityEnum): string {
    return this.entityLabels[entity] ?? entity;
  }

  getTabLabel(tab: ActionEnum): string {
    return tab === ActionEnum.CREATE ? "Crear" : "Editar";
  }

  getFieldControls(entity: string, action: ActionEnum) {
    const formArray = this.formConfigs[entity]?.[action]?.get(
      "fields",
    ) as FormArray;
    return formArray ? formArray.controls : [];
  }

  onFieldChanged(
    fieldId: string,
    formGroup: FormGroup,
    entity: string,
    action: ActionEnum,
  ) {
    const current = {
      label: formGroup.get("label")?.value,
      isVisible: formGroup.get("visible")?.value,
      isRequired: formGroup.get("required")?.value,
      isMandatory: formGroup.get("mandatory")?.value,
    };

    const originalStr = formGroup.get("_original")?.value;
    const original = JSON.parse(originalStr);

    if (
      current.label === original.label &&
      current.isVisible === original.visible &&
      current.isRequired === original.required
    ) {
      return;
    }

    const id = this.formConfigs[entity][action].get("id")?.value;
    const payload: UpdateFieldPayload = {
      field: {
        fieldLabel: current.label,
        isVisible: current.isVisible,
        isRequired: current.isRequired,
        isMandatory: current.isMandatory,
      },
    };

    this.companyFieldCatalogService
      .updateField(id, fieldId, payload)
      .subscribe({
        next: () => {
          formGroup.get("_original")?.setValue(
            JSON.stringify({
              label: current.label,
              visible: current.isVisible,
              required: current.isRequired,
            }),
            { emitEvent: false },
          );
        },
        error: () => {
          this.snackBar.open("Error al guardar el campo", "Cerrar", {
            duration: 4000,
          });
        },
      });
  }
}
